import { useShallow } from 'zustand/react/shallow';
import { useMemo } from 'react';
import isMatch from 'es-toolkit/compat/isMatch';
import { useIsLipaPage } from '@pkgs/components/hooks/useIsLipaPage';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { defaultAdvancedFiltersState } from '@pkgs/components/hooks/stores/search/useAdvancedFiltersModified';
import { extractStateFromStore } from '@pkgs/components/hooks/stores/search/slices/utilSlice';
import { SKY_IMAGE_URL } from '@pkgs/components/consts/consts';
import { InlineLink } from '@ev-tech/ui-core-components/InlineLink';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { replaceLocaleInPath } from '@ev-tech/search-modules-api';

import { NoResultBannerActionButtons } from './NoResultBannerActionButtons';

import styles from './NoResultsBanner.module.css';

export const NoResultsBanner = ({ isAISearchError }: { isAISearchError?: boolean }) => {
  const { t, countryCode, options, ImageComponent } = useAppContext();
  const { language } = options;
  const isLipaPage = useIsLipaPage();

  const searchMode = useSearchStore((state) => state.searchMode);

  const isPrivateOffice = useSearchStore((state) => state.privateOffice);

  const hasResettableFilters = useSearchStore(useShallow((state) => !isMatch(extractStateFromStore(state), defaultAdvancedFiltersState)));

  const [headline, subtitle] = useMemo(() => {
    if (isPrivateOffice) {
      return [t('search.privateOffice.results.noResults.title'), t('search.privateOffice.results.noResults.description')];
    }
    if (isLipaPage) {
      return [t('search.licensePartner.results.noResults.title'), t('search.licensePartner.results.noResults.description.part1')];
    }

    if (isAISearchError) {
      return [t('search.aiSearch.result.error.title'), t('search.aiSearch.result.error.subTitle')];
    }
    return [t('search.results.noResults.title'), t(`search.results.noResults.description.${searchMode}`)];
  }, [isPrivateOffice, isLipaPage, t, searchMode, isAISearchError]);

  const contactLinkUrl = replaceLocaleInPath(t('search.results.noResults.contactLink'), language, countryCode);

  return (
    <div className={styles.container}>
      <ImageComponent src={SKY_IMAGE_URL} alt="Sky" width={129} height={106} />
      <div className={styles.contentWrapper}>
        <div className={styles.textContainer}>
          <Headline className={styles.headline} label={headline} size="h5" />
          <Copy className={styles.subtitle} text={subtitle} size="l" />
        </div>
        {!isAISearchError && <NoResultBannerActionButtons hasResettableFilters={hasResettableFilters} />}
        {hasResettableFilters && !isPrivateOffice && !isLipaPage && !isAISearchError && (
          <InlineLink as="a" href={contactLinkUrl} label={t('search.results.noResults.button.contactAgent')} />
        )}
      </div>
    </div>
  );
};
